import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { BlackjackEvents, GameEvent, RoundResult } from 'game-logic';
import { delay } from 'src/games/common/helper';
import { AvailableFontColors, AvailableFontSizes, TextService } from '../../services/text-service';
import { UxMetrics } from '../../model/ux-metrics';
import { ResultsViewModel } from './results-view-model';

export class ResultsView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: ResultsViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.setName(ResultsView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const xPos = layoutConfig.boardWidth * 0.5;
    const yPos = layoutConfig.boardHeight * 0.4;

    this.setPosition(
      xPos * scalingMetrics.scaleFactor + scalingMetrics.scaledOffsetX,
      yPos * scalingMetrics.scaleFactor,
    );
  }

  private async handleLogicEvents(event: GameEvent<BlackjackEvents>): Promise<void> {
    // Don't process instant events
    if (event.instant) {
      return;
    }

    switch (event.type) {
      case 'onSetResult':
        await delay(UxMetrics.ResultDelay);
        await this.animateResult(event.payload.result);
        await delay(UxMetrics.ResultChipCreation + UxMetrics.ResultChipCollection);
        break;
      default:
        break;
    }
  }

  private async animateResult(result: RoundResult): Promise<void> {
    if (!this.currentResizeMetrics) {
      return;
    }

    const scaleFactor = this.currentResizeMetrics.scalingMetrics.scaleFactor;

    const boardWidth = this.currentResizeMetrics.layoutConfig.boardWidth;
    const tweenDistance = boardWidth * 0.2 * scaleFactor;

    const xLargeFontStyle = TextService.fontStyleBold(
      AvailableFontSizes.XXLarge,
      AvailableFontColors.Offwhite,
      this.viewModel.defaultFontFamily,
    );

    const levelTitleLabel = this.scene.add
      .text(this.x - tweenDistance, this.y, this.viewModel.resultLabel(result), xLargeFontStyle)
      .setFontSize(this.currentResizeMetrics.fontMetrics.XXLarge)
      .setOrigin(0.5, 0.5)
      .setAlpha(0.0)
      .setDepth(600);

    this.scene.tweens.add({
      targets: [levelTitleLabel],
      x: this.x,
      alpha: 1.0,
      duration: UxMetrics.ResultAnimateIn,
      ease: 'Quad.Out',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [levelTitleLabel],
          x: this.x + tweenDistance * 2,
          alpha: 0,
          duration: UxMetrics.ResultAnimateOut,
          ease: 'Quart.In',
          onComplete: () => {
            levelTitleLabel.destroy();
          },
        });
      },
    });

    const levelTitleBackground = this.scene.add
      .rectangle(
        this.x,
        this.y,
        boardWidth * scaleFactor,
        boardWidth * 0.5 * scaleFactor,
        this.viewModel.backgroundColor,
      )
      .setOrigin(0.5, 0.5)
      .setAlpha(0.0)
      .setDepth(599);

    this.scene.tweens.add({
      targets: [levelTitleBackground],
      alpha: 0.95,
      duration: UxMetrics.ResultAnimateIn,
      ease: 'Quad.Out',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [levelTitleBackground],
          alpha: 0,
          duration: UxMetrics.ResultAnimateOut,
          ease: 'Quart.In',
          onComplete: () => {
            levelTitleBackground.destroy();
          },
        });
      },
    });

    await delay(UxMetrics.ResultAnimateIn + UxMetrics.ResultAnimateOut);
  }
}
